import { useRedeemBarcodesMutation } from 'orders/reduxQueries'
import { delay } from 'utils'
import { CreateExceptionBarcodeError } from 'orders/ordersService'
import { ReplaceMessagesFunc } from 'messagesContext'
import { useTranslation } from 'translations'

interface useRedeemBarcodesProps {
    accountSlug: string
    hideMessage: (id: string) => void
    replaceMessages: ReplaceMessagesFunc
}

const useRedeemBarcodes = ({ accountSlug, replaceMessages, hideMessage }: useRedeemBarcodesProps) => {
    const { t } = useTranslation()
    const [redeemBarcodes] = useRedeemBarcodesMutation()

    const redeem = async (barcodes: string[]) => {
        const responseData = await redeemBarcodes({ accountSlug, barcodes: barcodes }).unwrap()

        if (responseData) {
            responseData.ifFailure((error: CreateExceptionBarcodeError) =>
                handleBarcodeRedeemFailure(error, barcodes.length),
            )
            responseData.ifSuccess(async () => await handleBarcodeRedeemSuccess(barcodes))
        }
    }

    const handleBarcodeRedeemFailure = (error: CreateExceptionBarcodeError, count: number) => {
        const getErrorKeys = () => Object.keys(error).filter((key) => !['type', 'barcodes'].includes(key))
        const hasErrors = () => getErrorKeys().filter((key) => !!error[key as keyof CreateExceptionBarcodeError])
        const getErrorBarcodes = () =>
            getErrorKeys()
                .map((key) => error[key as keyof CreateExceptionBarcodeError])
                .filter((value) => !!value)
                .join(', ')

        if (hasErrors()) {
            replaceMessages(
                error.type,
                'error',
                t('orders.orders.redeemTickets.failedItems', { count, barcodes: getErrorBarcodes() }),
            )
        } else {
            replaceMessages(error.type, 'error', t('orders.orders.redeemTickets.failed', { count }))
        }
    }

    const handleBarcodeRedeemSuccess = async (barcodes: string[]) => {
        const barcodesString = barcodes.join(', ')
        replaceMessages(
            'redeemed_successfully',
            'success',
            t('orders.orders.redeemTickets.success', { count: barcodes.length, barcodes: barcodesString }),
        )
        await delay(3000)
        hideMessage('redeemed_successfully')
    }

    return {
        redeem,
    }
}

export default useRedeemBarcodes
